import React, { useCallback, useMemo } from 'react';
import { generatePath } from 'react-router-dom';

import { type UseFormReturn, useForm } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';

import { CheckListElementProps } from 'components/CheckListElement';
import { URLS } from 'config';
import { getLenderName, scrollToTop } from 'utils';

import { HUMM_LIST_PROPS, LIST_PROPS } from './constants';

import { useApplyContext } from '../context';

export interface ApplyTermsAndConditionsFormValues {
  acceptTermsAndConditions: boolean;
}

export interface ApplyTermsAndConditionsFormHook {
  form: UseFormReturn<ApplyTermsAndConditionsFormValues>;
  onSubmit: (
    formValues: StringIndexedObject<ApplyTermsAndConditionsFormValues>,
  ) => void;
  isLoading: boolean;
  disabled: boolean;
  listProps: CheckListElementProps;
}

export default function useApplyTermsAndConditionsForm(): ApplyTermsAndConditionsFormHook {
  const { isLoading, id, provider, goToPage } = useApplyContext();

  const form = useForm<ApplyTermsAndConditionsFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<ApplyTermsAndConditionsFormValues>) => {
      const accept = (
        formValues as unknown as ApplyTermsAndConditionsFormValues
      ).acceptTermsAndConditions;
      if (!accept || isLoading || !id) return;
      goToPage('YOUR_INFO');
      const url = new URL(window.location.href);
      const nextUrl = `${url.origin}${generatePath(
        URLS.applyForFinanceDetails,
        {
          id,
        },
      )}`;
      window.history.pushState(window.history.state, '', nextUrl);
      setTimeout(() => scrollToTop(), 10);
    },
    [isLoading, goToPage, id],
  );

  const accept = form.watch('acceptTermsAndConditions');

  const disabled = useMemo(() => !accept, [accept]);

  const updatedListProps = useMemo(
    () => ({
      className: LIST_PROPS.className,
      items: LIST_PROPS.items.map((item) => {
        const updatedChildren = React.Children.map(
          item.props.children,
          (child) => {
            if (typeof child === 'string' && child.includes('$LENDER')) {
              return child.replaceAll('$LENDER', getLenderName(provider));
            }
            return child;
          },
        );
        return React.cloneElement(item, {}, updatedChildren);
      }),
    }),
    [provider],
  );

  const listProps = useMemo(
    () => ({
      ...(provider === 'humm' ? HUMM_LIST_PROPS : updatedListProps),
      isLoading,
    }),
    [provider, updatedListProps, isLoading],
  );

  return {
    form,
    onSubmit,
    disabled,
    isLoading,
    listProps,
  };
}
